import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function ChatGroups() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  // Chat groups state
  const [chatGroups, setChatGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to latest message whenever messages change or group changes
  useEffect(() => {
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } catch {
        // Fallback without smooth behavior if not supported
        messagesEndRef.current.scrollIntoView();
      }
    }
  }, [groupMessages, selectedGroup]);
  
  // Create group form state
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Data for selection
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  
  // Edit group members state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchChatGroups();
      fetchClasses();
      fetchAllTeachers();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (selectedClass) {
      fetchStudentsForClass();
      fetchTeachersForClass();
      // Get sections for all classes with the same base name
      const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
      if (selectedClassData) {
        const getBaseClassName = (className) => {
          if (!className) return '';
          if (className.includes(' - ')) {
            return className.split(' - ')[0].trim();
          }
          return className.trim();
        };
        
        const baseName = getBaseClassName(selectedClassData.class_name);
        // Get all classes with the same base name
        const classesWithSameBase = classes.filter(c => {
          const cBaseName = getBaseClassName(c.class_name);
          return cBaseName === baseName;
        });
        
        // Collect all unique sections from all classes with the same base name
        const allSections = new Set();
        classesWithSameBase.forEach(cls => {
          if (cls.sections && Array.isArray(cls.sections)) {
            cls.sections.filter(Boolean).forEach(section => allSections.add(section));
          }
        });
        
        setSections(Array.from(allSections));
      } else {
        setSections([]);
      }
    } else {
      setStudents([]);
      setTeachers([]);
      setSections([]);
    }
  }, [selectedClass, classes]);

  const fetchChatGroups = async () => {
    if (!schoolData?.id) return;
    try {
      setLoadingGroups(true);
      let query = supabase
        .from('chat_groups')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: false });

      if (selectedBranchId) {
        // If branch filtering is needed, you might need to join with classes table
        // For now, we'll fetch all groups
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching chat groups:', error);
        return;
      }

      // Enrich groups with member counts
      const enrichedGroups = await Promise.all(
        (data || []).map(async (group) => {
          const { data: members } = await supabase
            .from('chat_group_members')
            .select('id')
            .eq('group_id', group.id);

          const { data: lastMessage } = await supabase
            .from('chat_messages')
            .select('created_at, content')
            .eq('group_id', group.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          return {
            ...group,
            memberCount: members?.length || 0,
            lastMessage: lastMessage?.content || null,
            lastMessageTime: lastMessage?.created_at || null
          };
        })
      );

      setChatGroups(enrichedGroups);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchClasses = async () => {
    if (!schoolData?.id) return;
    try {
      setLoadingData(true);
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('class_name');

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchAllTeachers = async () => {
    if (!schoolData?.id) return;
    try {
      const { data, error } = await supabase
        .from('teachers')
        .select('id, first_name, last_name, email, is_active')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('first_name');

      if (error) {
        console.error('Error fetching teachers:', error);
        return;
      }

      setAllTeachers(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchStudentsForClass = async () => {
    if (!schoolData?.id) return;
    
    // If no class is selected, don't fetch students
    if (!selectedClass) {
      setStudents([]);
      return;
    }
    
    try {
      setLoadingData(true);
      
      // Get the selected class to find its base name
      const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
      if (!selectedClassData) {
        setStudents([]);
        setLoadingData(false);
        return;
      }
      
      // Get base class name (before " - ")
      const getBaseClassName = (className) => {
        if (!className) return '';
        if (className.includes(' - ')) {
          return className.split(' - ')[0].trim();
        }
        return className.trim();
      };
      
      const baseName = getBaseClassName(selectedClassData.class_name);
      
      // Find all class IDs with the same base name
      const classIdsWithSameBase = classes
        .filter(c => {
          const cBaseName = getBaseClassName(c.class_name);
          return cBaseName === baseName;
        })
        .map(c => c.id);
      
      // Build query to fetch students from all classes with the same base name
      let query = supabase
        .from('students')
        .select('id, first_name, last_name, email, section_id')
        .eq('school_id', schoolData.id)
        .in('class_id', classIdsWithSameBase)
        .eq('is_active', true);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('first_name', { ascending: true });

      if (error) {
        console.error('Error fetching students:', error);
        setStudents([]);
        setLoadingData(false);
        return;
      }

      // Filter by section in JavaScript if section is selected
      // This handles case-insensitive matching and different field formats
      let filteredStudents = data || [];
      
      if (selectedSection && selectedSection.trim() !== '') {
        filteredStudents = filteredStudents.filter(student => {
          const studentSection = student.section_id;
          if (!studentSection) return false;
          
          // Case-insensitive comparison
          const sectionMatch = String(studentSection).trim().toLowerCase() === String(selectedSection).trim().toLowerCase();
          
          return sectionMatch;
        });
      }

      console.log('Fetched students:', {
        total: data?.length || 0,
        filtered: filteredStudents.length,
        selectedSection,
        selectedClass,
        students: filteredStudents.map(s => ({
          name: `${s.first_name} ${s.last_name}`,
          section_id: s.section_id
        }))
      });

      setStudents(filteredStudents);
    } catch (err) {
      console.error('Error fetching students:', err);
      setStudents([]);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchTeachersForClass = async () => {
    if (!schoolData?.id || !selectedClass) return;
    try {
      // Get the selected class to find its base name
      const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
      if (!selectedClassData) {
        setTeachers([]);
        return;
      }
      
      // Get base class name (before " - ")
      const getBaseClassName = (className) => {
        if (!className) return '';
        if (className.includes(' - ')) {
          return className.split(' - ')[0].trim();
        }
        return className.trim();
      };
      
      const baseName = getBaseClassName(selectedClassData.class_name);
      
      // Find all class IDs with the same base name
      const classIdsWithSameBase = classes
        .filter(c => {
          const cBaseName = getBaseClassName(c.class_name);
          return cBaseName === baseName;
        })
        .map(c => c.id);
      
      // Get teachers from all classes with the same base name
      const { data: classesData } = await supabase
        .from('classes')
        .select('teachers, subjects')
        .in('id', classIdsWithSameBase);

      // Collect all unique teacher IDs from all classes with the same base name
      const allTeacherIds = new Set();
      classesData?.forEach(classData => {
        if (classData.teachers && Array.isArray(classData.teachers)) {
          classData.teachers.forEach(teacherId => {
            if (teacherId) allTeacherIds.add(teacherId);
          });
        }
      });

      const teacherIds = Array.from(allTeacherIds);
      
      if (teacherIds.length > 0) {
        const { data, error } = await supabase
          .from('teachers')
          .select('id, first_name, last_name, email')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .in('id', teacherIds)
          .order('first_name');

        if (!error && data) {
          setTeachers(data);
        }
      } else {
        setTeachers([]);
      }
    } catch (err) {
      console.error('Error:', err);
      setTeachers([]);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    if (!groupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (selectedStudents.length === 0 && selectedTeachers.length === 0) {
      alert('Please select at least one student or teacher');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create the chat group
      const groupData = {
        school_id: schoolData.id,
        name: groupName.trim(),
        description: groupDescription.trim() || null,
        group_type: selectedClass ? 'class' : 'custom',
        class_id: selectedClass ? parseInt(selectedClass) : null,
        section_name: selectedSection || null,
        created_by: 'school'
      };

      const { data: newGroup, error: groupError } = await supabase
        .from('chat_groups')
        .insert(groupData)
        .select()
        .single();

      if (groupError) {
        throw groupError;
      }

      // Add members to the group
      const members = [];

      // Add selected students
      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);
        if (student) {
          members.push({
            group_id: newGroup.id,
            user_type: 'student',
            user_id: studentId.toString(),
            display_name: `${student.first_name} ${student.last_name}`
          });
        }
      }

      // Add selected teachers
      for (const teacherId of selectedTeachers) {
        const teacher = allTeachers.find(t => t.id === teacherId) || teachers.find(t => t.id === teacherId);
        if (teacher) {
          members.push({
            group_id: newGroup.id,
            user_type: 'teacher',
            user_id: teacherId.toString(),
            display_name: `${teacher.first_name} ${teacher.last_name}`
          });
        }
      }

      if (members.length > 0) {
        const { error: membersError } = await supabase
          .from('chat_group_members')
          .insert(members);

        if (membersError) {
          throw membersError;
        }
      }

      // Reset form
      setGroupName('');
      setGroupDescription('');
      setSelectedClass('');
      setSelectedSection('');
      setSelectedStudents([]);
      setSelectedTeachers([]);
      setShowCreateModal(false);

      // Refresh groups list
      await fetchChatGroups();

      alert('Chat group created successfully!');
    } catch (err) {
      console.error('Error creating chat group:', err);
      alert(`Failed to create chat group: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this chat group? All messages will be deleted.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('chat_groups')
        .delete()
        .eq('id', groupId);

      if (error) throw error;

      // Close chat if the deleted group was open
      if (selectedGroup && selectedGroup.id === groupId) {
        setSelectedGroup(null);
        setGroupMessages([]);
      }

      await fetchChatGroups();
      alert('Chat group deleted successfully!');
    } catch (err) {
      console.error('Error deleting chat group:', err);
      alert(`Failed to delete chat group: ${err.message}`);
    }
  };

  const openEditModal = async (group) => {
    setEditingGroup(group);
    setShowEditModal(true);
    await loadGroupMembers(group.id);
  };

  const loadGroupMembers = async (groupId) => {
    if (!groupId) return;

    try {
      setLoadingMembers(true);
      const { data: members, error } = await supabase
        .from('chat_group_members')
        .select('*')
        .eq('group_id', groupId)
        .order('added_at', { ascending: true });

      if (error) throw error;

      // Enrich members with full details
      const enrichedMembers = await Promise.all(
        (members || []).map(async (member) => {
          let fullName = member.display_name || 'Unknown';
          let email = null;

          if (member.user_type === 'student') {
            const { data: student } = await supabase
              .from('students')
              .select('first_name, last_name, email')
              .eq('id', member.user_id)
              .single();
            if (student) {
              fullName = `${student.first_name} ${student.last_name}`;
              email = student.email;
            }
          } else if (member.user_type === 'teacher') {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name, email')
              .eq('id', member.user_id)
              .single();
            if (teacher) {
              fullName = `${teacher.first_name} ${teacher.last_name}`;
              email = teacher.email;
            }
          } else if (member.user_type === 'parent') {
            const { data: parent } = await supabase
              .from('parents')
              .select('first_name, last_name, email')
              .eq('id', member.user_id)
              .single();
            if (parent) {
              fullName = `${parent.first_name} ${parent.last_name}`;
              email = parent.email;
            }
          }

          return {
            ...member,
            fullName,
            email
          };
        })
      );

      setGroupMembers(enrichedMembers);
    } catch (err) {
      console.error('Error loading group members:', err);
      alert('Failed to load group members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member from the group?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('chat_group_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      // Reload members
      await loadGroupMembers(editingGroup.id);
      
      // Refresh groups list to update member count
      await fetchChatGroups();
      
      alert('Member removed successfully!');
    } catch (err) {
      console.error('Error removing member:', err);
      alert(`Failed to remove member: ${err.message}`);
    }
  };

  const openGroupChat = async (group) => {
    setSelectedGroup(group);
    setNewMessage('');
    await loadGroupMessages(group.id);
  };

  const loadGroupMessages = async (groupId) => {
    if (!groupId || !schoolData?.id) return;

    try {
      setLoadingMessages(true);
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', groupId)
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error loading messages:', error);
        return;
      }

      // Enrich messages with sender names
      const enrichedMessages = await Promise.all(
        (data || []).map(async (message) => {
          let senderName = 'Unknown';
          
          if (message.sender_type === 'student') {
            const { data: student } = await supabase
              .from('students')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (student) {
              senderName = `${student.first_name} ${student.last_name}`;
            }
          } else if (message.sender_type === 'teacher') {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (teacher) {
              senderName = `${teacher.first_name} ${teacher.last_name}`;
            }
          } else if (message.sender_type === 'parent') {
            const { data: parent } = await supabase
              .from('parents')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (parent) {
              senderName = `${parent.first_name} ${parent.last_name}`;
            }
          } else if (message.sender_type === 'school') {
            senderName = 'School Admin';
          }

          return {
            ...message,
            senderName
          };
        })
      );

      setGroupMessages(enrichedMessages);
    } catch (err) {
      console.error('Error loading group messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Upload files to Supabase storage
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return [];
    
    const uploadedUrls = [];
    setUploadingFiles(true);
    
    try {
      const bucketName = 'chat-uploads';
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `chat_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${schoolData.id}/${fileName}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          if (uploadError.message.includes('Bucket not found')) {
            alert('Storage bucket not found. Please create "chat-uploads" bucket in Supabase Storage.');
            continue;
          }
          continue;
        }
        
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(filePath);
        
        uploadedUrls.push({
          url: urlData.publicUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      }
    } catch (err) {
      console.error('Error uploading files:', err);
    } finally {
      setUploadingFiles(false);
    }
    
    return uploadedUrls;
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedGroup || !schoolData || sendingMessage) return;

    try {
      setSendingMessage(true);
      
      let mediaUrls = [];
      if (selectedFiles.length > 0) {
        mediaUrls = await uploadFiles(selectedFiles);
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const { data: message, error } = await supabase
        .from('chat_messages')
        .insert({
          school_id: schoolData.id,
          group_id: selectedGroup.id,
          sender_type: 'school',
          sender_id: schoolData.id.toString(),
          content: newMessage.trim() || null,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null
        })
        .select()
        .single();

      if (error) throw error;

      // Add to messages with sender name
      const newMessageWithSender = {
        ...message,
        senderName: 'School Admin'
      };

      setGroupMessages(prev => [...prev, newMessageWithSender]);
      setNewMessage('');

      // Refresh groups to update last message
      await fetchChatGroups();
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    }).slice(0, 5);
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleTeacherSelection = (teacherId) => {
    setSelectedTeachers(prev => 
      prev.includes(teacherId)
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const selectAllTeachers = () => {
    const availableTeachers = selectedClass ? teachers : allTeachers;
    if (selectedTeachers.length === availableTeachers.length) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(availableTeachers.map(t => t.id));
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <SchoolSidebar schoolData={schoolData} />

      <div className="main-content">
        <div className="content-header">
          <h1>💬 Chat Groups</h1>
          <button 
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ Create Chat Group
          </button>
        </div>

        <div className="content-body">
          {loadingGroups ? (
            <div className="loading-container">
              <p>Loading chat groups...</p>
            </div>
          ) : chatGroups.length === 0 ? (
            <div className="empty-state">
              <p>No chat groups yet. Create your first chat group to get started! 🚀</p>
            </div>
          ) : (
            <div className="chat-groups-list">
              {chatGroups.map(group => (
                <div 
                  key={group.id} 
                  className="chat-group-card"
                  onClick={() => openGroupChat(group)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="chat-group-header">
                    <h3>👥 {group.name}</h3>
                    <div className="chat-group-actions">
                      <button
                        className="btn-edit-small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(group);
                        }}
                        title="Edit group members"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn-danger-small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGroup(group.id);
                        }}
                        title="Delete this chat group"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                  {group.description && (
                    <p className="chat-group-description">{group.description}</p>
                  )}
                  <div className="chat-group-info">
                    <span>👤 Members: {group.memberCount}</span>
                    {group.class_id && (
                      <span>📚 Class: {classes.find(c => c.id === group.class_id)?.class_name || 'N/A'}</span>
                    )}
                    {group.section_name && (
                      <span>📋 Section: {group.section_name}</span>
                    )}
                    {group.lastMessageTime && (
                      <span>💬 Last message: {new Date(group.lastMessageTime).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => !isSubmitting && setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>➕ Create Chat Group</h2>
              <button 
                className="modal-close"
                onClick={() => !isSubmitting && setShowCreateModal(false)}
                disabled={isSubmitting}
                title="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateGroup} className="create-group-form">
              <div className="form-group">
                <label>Group Name *</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Enter group description"
                  rows="3"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label>Class (Optional - for filtering students/teachers)</label>
                <select
                  value={selectedClass}
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setSelectedSection('');
                    setSelectedStudents([]);
                    setSelectedTeachers([]);
                  }}
                  disabled={isSubmitting || loadingData}
                >
                  <option value="">Select a class (optional)</option>
                  {(() => {
                    // Extract base class names (before " - ") and get unique classes
                    const getBaseClassName = (className) => {
                      if (!className) return '';
                      // If class name contains " - ", take only the part before it
                      if (className.includes(' - ')) {
                        return className.split(' - ')[0].trim();
                      }
                      return className.trim();
                    };

                    // Group classes by base class name
                    const classesByBaseName = {};
                    classes.forEach(cls => {
                      const baseName = getBaseClassName(cls.class_name);
                      if (baseName) {
                        if (!classesByBaseName[baseName]) {
                          classesByBaseName[baseName] = [];
                        }
                        classesByBaseName[baseName].push(cls);
                      }
                    });

                    // Get unique base class names and use the first class ID for each
                    const uniqueClasses = Object.keys(classesByBaseName).map(baseName => ({
                      baseName,
                      classId: classesByBaseName[baseName][0].id,
                      allClassIds: classesByBaseName[baseName].map(c => c.id)
                    }));

                    return uniqueClasses.map(uniqueClass => (
                      <option key={uniqueClass.classId} value={uniqueClass.classId}>
                        {uniqueClass.baseName}
                      </option>
                    ));
                  })()}
                </select>
              </div>

              {selectedClass && sections.length > 0 && (
                <div className="form-group">
                  <label>Section (Optional)</label>
                  <select
                    value={selectedSection}
                    onChange={(e) => {
                      setSelectedSection(e.target.value);
                      setSelectedStudents([]);
                    }}
                    disabled={isSubmitting}
                  >
                    <option value="">All Sections</option>
                    {sections.map((section, index) => (
                      <option key={index} value={section}>
                        {section}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Students Selection */}
              <div className="form-group">
                <div className="selection-header">
                  <label>Select Students *</label>
                  {students.length > 0 && (
                    <button
                      type="button"
                      className="btn-link"
                      onClick={selectAllStudents}
                    >
                      {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
                <div className="selection-box">
                  {!selectedClass ? (
                    <div className="empty-selection">
                      Select a class to see students
                    </div>
                  ) : loadingData ? (
                    <div>Loading students...</div>
                  ) : students.length === 0 ? (
                    <div className="empty-selection">
                      {selectedSection ? `No students found in ${selectedSection} section` : 'No students found for this class'}
                    </div>
                  ) : (
                    students.map(student => (
                      <label key={student.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => toggleStudentSelection(student.id)}
                          disabled={isSubmitting}
                        />
                        <span>{student.first_name} {student.last_name} {student.section_id ? `(${student.section_id})` : ''}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Teachers Selection */}
              <div className="form-group">
                <div className="selection-header">
                  <label>Select Teachers *</label>
                  {(selectedClass ? teachers : allTeachers).length > 0 && (
                    <button
                      type="button"
                      className="btn-link"
                      onClick={selectAllTeachers}
                    >
                      {selectedTeachers.length === (selectedClass ? teachers : allTeachers).length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
                <div className="selection-box">
                  {loadingData ? (
                    <div>Loading teachers...</div>
                  ) : (selectedClass ? teachers : allTeachers).length === 0 ? (
                    <div className="empty-selection">
                      {selectedClass ? 'No teachers found for this class' : 'No teachers available'}
                    </div>
                  ) : (
                    (selectedClass ? teachers : allTeachers).map(teacher => (
                      <label key={teacher.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedTeachers.includes(teacher.id)}
                          onChange={() => toggleTeacherSelection(teacher.id)}
                          disabled={isSubmitting}
                        />
                        <span>{teacher.first_name} {teacher.last_name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting || selectedStudents.length === 0 && selectedTeachers.length === 0}
                >
                  {isSubmitting ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Members Modal */}
      {showEditModal && editingGroup && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>✏️ Edit Group: {editingGroup.name}</h2>
              <button 
                className="modal-close"
                onClick={() => setShowEditModal(false)}
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="edit-members-container">
              {loadingMembers ? (
                <div className="loading-container">
                  <p>Loading members...</p>
                </div>
              ) : groupMembers.length === 0 ? (
                <div className="empty-state">
                  <p>No members in this group.</p>
                </div>
              ) : (
                <>
                  <div className="members-list-header">
                    <h3>Group Members ({groupMembers.length})</h3>
                    <p className="members-subtitle">Click remove to remove a member from this group</p>
                  </div>
                  <div className="members-list">
                    {groupMembers.map(member => (
                      <div key={member.id} className="member-item">
                        <div className="member-info">
                          <div className="member-name">
                            <span className="member-type-badge">
                              {member.user_type === 'student' ? '👨‍🎓' : 
                               member.user_type === 'teacher' ? '👨‍🏫' : 
                               member.user_type === 'parent' ? '👨‍👩‍👧‍👦' : '👤'}
                            </span>
                            <strong>{member.fullName}</strong>
                            <span className="member-type">({member.user_type})</span>
                          </div>
                          {member.email && (
                            <div className="member-email">{member.email}</div>
                          )}
                        </div>
                        <button
                          className="btn-danger-small"
                          onClick={() => removeMember(member.id)}
                          title="Remove this member"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="form-actions" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Chat Modal */}
      {selectedGroup && (
        <div className="modal-overlay" onClick={() => setSelectedGroup(null)}>
          <div className="modal-content chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>💬 {selectedGroup.name}</h2>
              <button 
                className="modal-close"
                onClick={() => setSelectedGroup(null)}
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="chat-messages-container">
              {loadingMessages ? (
                <div className="chat-loading">
                  <div>Loading messages...</div>
                </div>
              ) : groupMessages.length === 0 ? (
                <div className="chat-empty">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <div className="chat-messages-list">
                  {groupMessages.map(message => (
                    <div
                      key={message.id}
                      className={`chat-message-item ${message.sender_type === 'school' ? 'sent' : 'received'}`}
                    >
                      <div className="message-content">
                        <div className="message-sender">{message.senderName}</div>
                        {message.content && <div className="message-text">{message.content}</div>}
                        {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                          <div className="chat-message-media" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {message.media_urls.map((media, idx) => (
                              <div key={idx}>
                                {media.type && media.type.startsWith('image/') ? (
                                  <img
                                    src={media.url}
                                    alt={media.name || 'Image'}
                                    style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                    onClick={() => window.open(media.url, '_blank')}
                                  />
                                ) : (
                                  <a
                                    href={media.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '8px 12px',
                                      background: '#F3F4F6',
                                      borderRadius: '8px',
                                      textDecoration: 'none',
                                      color: '#1F2937',
                                      maxWidth: '300px'
                                    }}
                                  >
                                    <span>📄</span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {media.name || 'Document'}
                                    </span>
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="message-time">
                          {new Date(message.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <form onSubmit={sendMessage} className="chat-input-form">
              {selectedFiles.length > 0 && (
                <div className="chat-selected-files" style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#E5E7EB', borderRadius: '4px', fontSize: '12px' }}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="chat-file-input-groups"
                />
                <label
                  htmlFor="chat-file-input-groups"
                  style={{ cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '8px', minWidth: '40px', flexShrink: 0 }}
                  title="Attach file"
                >
                  📎
                </label>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  disabled={sendingMessage || uploadingFiles}
                  className="chat-input"
                  style={{ flex: 1, minWidth: 0, width: '100%' }}
                />
                <button
                  type="submit"
                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || sendingMessage || uploadingFiles}
                  className="chat-send-btn"
                >
                  {(sendingMessage || uploadingFiles) ? '⏳' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatGroups;
